import React, { useState, useMemo } from 'react';
import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import { useParams, useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import { message, Popover, QRCode, Typography, Divider, Button, Modal, Table, Space } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import API from 'helpers/API';
import { phoneNumber } from 'utils/Formatter';
import { getReclaimStatus, getTankUseStatus, getTankChargeStatus } from 'utils/CodeMapper';
import { BottomButtons, HeadingH1, UnitTable } from 'components/ui';
import { buildTankAddModalColumns } from 'pages/tanks/tankAddModalColumns';

import styles from './tanks.module.scss';

const cx = classNames.bind(styles);

const sortText = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return String(a).localeCompare(String(b));
};

const Page = () => {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [reclaim, setReclaim] = useState();
  const [reclaimTanks, setReclaimTanks] = useState();
  const [tanks, setTanks] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const loadReclaim = () => {
    API.get(`/reclaims/${rid}`).then(({ success, data }) => {
      if (success) {
        setReclaim(data);
        setReclaimTanks(data.reclaimTanks);
      }
    });
  };

  const getTanks = () => {
    API.get('/tanks').then(({ success, data }) => {
      setTanks(success ? data.tanks : []);
    });
  };

  // 용기 선택
  const selectTank = (tank) => {
    if (tank) {
      // API: 회수-용기 할당
      API.post(`/reclaims/${rid}/tanks/${tank.rid}`).then(({ success, data }) => {
        if (success) {
          message.success({
            key: 'addNewTank',
            content: '추가 완료되었습니다.',
          });
          loadReclaim();
          getTanks();
        } else {
          message.error('회수 가능한 용기가 아닙니다.');
        }
      });
    }
  };

  // 용기 해제
  const deselectTank = (item) => {
    // API: 회수-용기 삭제
    API.delete(`/reclaims/${rid}/tanks/${item.tank.rid}`).then(({ success }) => {
      if (success) {
        message.success('제거되었습니다.');
        loadReclaim();
        getTanks();
      }
    });
  };

  const downloadQRCode = (id) => {
    const canvas = document.getElementById(id)?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL();
      const a = document.createElement('a');
      a.download = `${id}_QRCode.png`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const filteredTanks = useMemo(() => {
    return tanks.filter((data) => data.useStatus === 'INUSE' && data.location.name === '주문고객');
  }, [tanks]);

  useMountEffect(() => {
    loadReclaim();
    getTanks();
  }, [rid]);

  return (
    <>
      <div className={cx('container')}>
        <Space direction="vertical" size={16} className={cx('wrapper')}>
          <HeadingH1 title={'용기 관리'} />

          <Typography.Text strong className={cx('title')}>
            회수 정보
          </Typography.Text>
          <div className={cx('sectionPanel')}>
            <div className={cx('infoGridThree')}>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>상태</span>
                <span className={cx('metaValue')}>{getReclaimStatus(reclaim?.status)}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>수량</span>
                <span className={cx('metaValue')}>{Number(reclaim?.quantity).toLocaleString() || '-'}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>성명</span>
                <span className={cx('metaValue')}>{reclaim?.customerName ?? '-'}</span>
              </div>
            </div>
            <div className={cx('infoGridThree')}>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>회사명</span>
                <span className={cx('metaValue')}>{reclaim?.customerCompany ?? '-'}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>전화번호</span>
                <span className={cx('metaValue')}>{phoneNumber(reclaim?.customerPhone) || '-'}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>주소</span>
                <span className={cx('metaValue')}>{`${reclaim?.customerAddress ?? ''} ${reclaim?.customerAddressDetail ?? ''}`.trim() || '-'}</span>
              </div>
            </div>
          </div>

          <Divider />

          <Typography.Text strong className={cx('title')}>
            용기 내역
          </Typography.Text>
          <div className={cx('detailCard')}>
            <div className={cx('detailCardHeader')}>
              <Typography.Text strong>용기내역</Typography.Text>
              <Button
                disabled={reclaim?.status === 'RECLAIMED' || reclaim?.status === 'SHIPPING' || filteredTanks?.length === 0}
                type="primary"
                onClick={() => setIsOpen((isPrev) => !isPrev)}
              >
                용기 추가
              </Button>
            </div>
            <UnitTable
              columns={[
                {
                  key: 'qrNo',
                  dataIndex: 'qrNo',
                  title: 'QR 번호',
                  width: 140,
                  ellipsis: true,
                  defaultSortOrder: 'ascend',
                  sorter: (a, b) => sortText(a.tank?.qrcode, b.tank?.qrcode),
                  render: (_, record) => record.tank?.qrcode ?? '-',
                },
                {
                  key: 'serialNo',
                  dataIndex: 'serialNo',
                  title: '용기 일련번호',
                  width: 140,
                  ellipsis: true,
                  sorter: (a, b) => sortText(a.tank?.serialNo, b.tank?.serialNo),
                  render: (_, record) => <Link to={`/tanks/${record.tank?.rid}`}>{record.tank?.serialNo ?? '-'}</Link>,
                },
                {
                  key: 'qrcode',
                  dataIndex: 'qrcode',
                  title: 'QR코드',
                  width: 84,
                  align: 'center',
                  render: (_, record) => {
                    const qrId = `${record?.tank?.rid}_QR`;
                    return (
                      <Popover
                        id={qrId}
                        overlayInnerStyle={{ padding: 0 }}
                        content={<QRCode size={200} value={record.tank?.qrcode} bordered={false} />}>
                        <Button onClick={() => downloadQRCode(qrId)} icon={<DownloadOutlined />} />
                      </Popover>
                    );
                  },
                },
                {
                  key: 'useStatus',
                  dataIndex: 'useStatus',
                  title: '가용상태',
                  sorter: (a, b) => sortText(getTankUseStatus(a.tank?.useStatus), getTankUseStatus(b.tank?.useStatus)),
                  render: (_, record) => getTankUseStatus(record.tank?.useStatus),
                  width: 76,
                  align: 'center',
                },
                {
                  key: 'chargeStatus',
                  dataIndex: 'chargeStatus',
                  title: '충전상태',
                  sorter: (a, b) =>
                    sortText(getTankChargeStatus(a.tank?.chargeStatus), getTankChargeStatus(b.tank?.chargeStatus)),
                  render: (_, record) => getTankChargeStatus(record.tank?.chargeStatus),
                  width: 76,
                  align: 'center',
                },
                {
                  key: 'chargeCount',
                  dataIndex: 'chargeCount',
                  title: '충전횟수',
                  width: 76,
                  align: 'right',
                  sorter: (a, b) => Number(a.tank?.chargeCount ?? 0) - Number(b.tank?.chargeCount ?? 0),
                  render: (_, record) => Number(record.tank?.chargeCount).toLocaleString(),
                },
                {
                  key: 'createdAt',
                  dataIndex: 'createdAt',
                  title: '할당일시',
                  sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
                  render: (_, record) => moment(record.createdAt).format('YYYY-MM-DD HH:mm:ss'),
                  width: 170,
                  align: 'center',
                },
                {
                  key: 'createdBy',
                  title: '할당자',
                  sorter: (a, b) => sortText(a.createdBy?.name, b.createdBy?.name),
                  render: (_, record) => (record.createdBy ? record.createdBy.name : '-'),
                  width: 96,
                  ellipsis: true,
                  align: 'center',
                },
                {
                  key: 'delete',
                  title: '제거',
                  width: 76,
                  align: 'center',
                  render: (_, record) => (
                    <Button
                      disabled={reclaim?.status === 'RECLAIMED' || reclaim?.status === 'SHIPPING'}
                      danger
                      type="primary"
                      size="small"
                      onClick={() => deselectTank(record)}>
                      제거
                    </Button>
                  ),
                },
              ]}
              dataSource={reclaimTanks}
              rowKey={(rowData) => rowData.tank?.rid}
              scroll={{ x: 936 }}
            />
          </div>

          <BottomButtons
            left={[
              <Button key={'prevMovePage'} onClick={() => navigate(-1)}>
                뒤로
              </Button>,
            ]}
          />
        </Space>
      </div>

      <Modal
        title="용기 추가"
        open={isOpen}
        width={1280}
        onCancel={() => setIsOpen((isPrev) => !isPrev)}
        maskClosable={false}
        footer={null}
      >
        <Table
          dataSource={filteredTanks}
          rowKey={'rid'}
          tableLayout="fixed"
          columns={buildTankAddModalColumns({
            getTankUseStatus,
            getTankChargeStatus,
            isAddDisabled: (tank) => reclaimTanks?.some((data) => data.tank?.rid === tank.rid) ?? false,
            onAddTank: (tank) => {
              selectTank(tank);
              setIsOpen((isPrev) => !isPrev);
            },
          })}
        />
      </Modal>
    </>
  );
};

export default Page;
